
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useAuthCallback = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<"verifying" | "success" | "error">("verifying");
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const handleAuthCallback = async () => {
    try {
      // Check for show_password_form query param (set by PASSWORD_RECOVERY event)
      const urlParams = new URLSearchParams(window.location.search);
      const showPasswordFormParam = urlParams.get("show_password_form");
      
      if (showPasswordFormParam === "true") {
        console.log("show_password_form param detected, showing password form");
        setVerificationStatus("success");
        setShowPasswordForm(true);
        setIsLoading(false);
        return;
      }
      
      // Check for hash fragment (used in password reset)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get("access_token");
      const refreshToken = hashParams.get("refresh_token");
      const tokenType = hashParams.get("type");
      
      console.log("Hash params:", { accessToken: !!accessToken, tokenType, refreshTokenExists: !!refreshToken });
      
      // Handle password recovery token in URL hash
      if (accessToken && tokenType === "recovery") {
        console.log("Processing password recovery from hash params");
        
        // Set the session with the access token
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken || "",
        });
        
        if (error) {
          console.error("Error setting session:", error);
          setVerificationStatus("error");
          toast({
            title: "Erreur de session",
            description: "Impossible de valider votre session. Veuillez réessayer.",
            variant: "destructive",
          });
          setTimeout(() => navigate("/auth"), 3000);
          return;
        }
        
        console.log("Session set successfully:", data);
        setVerificationStatus("success");
        setShowPasswordForm(true);
        setIsLoading(false);
        return;
      }
      
      // Get URL params for other auth types
      const url = new URL(window.location.href);
      const token = url.searchParams.get("token");
      const type = url.searchParams.get("type");
      const error = url.searchParams.get("error");
      const error_description = url.searchParams.get("error_description");
      
      console.log("Auth callback params:", { token, type, error, error_description });
      
      // Handle error in URL
      if (error) {
        console.error("Auth error:", error, error_description);
        toast({
          title: "Erreur d'authentification",
          description: error_description || "Une erreur s'est produite pendant l'authentification.",
          variant: "destructive",
        });
        setVerificationStatus("error");
        setTimeout(() => navigate("/auth"), 3000);
        return;
      }

      // Handle password recovery token
      if (token && type === "recovery") {
        console.log("Processing password recovery with token");
        
        // Redirect to password update page
        setVerificationStatus("success");
        setShowPasswordForm(true);
        setIsLoading(false);
        return;
      }

      // If we have a token in the URL, this is a direct verification link
      if (token && type === "signup") {
        console.log("Processing verification link with token");
        
        // The verification happens automatically when the page loads
        // Check if we now have a session
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          throw sessionError;
        }
        
        if (sessionData?.session) {
          setVerificationStatus("success");
          toast({
            title: "Inscription terminée",
            description: "Votre compte a été vérifié. Vous êtes maintenant connecté!",
          });
          
          // Rediriger vers la page d'accueil après 2 secondes
          setTimeout(() => navigate("/"), 2000);
          return;
        } else {
          // If no session, try to manually verify the token
          console.log("No session found, trying manual verification");
          
          // Give Supabase some time to process the verification
          setTimeout(async () => {
            const { data: retryData, error: retryError } = await supabase.auth.getSession();
            
            if (!retryError && retryData?.session) {
              setVerificationStatus("success");
              toast({
                title: "Inscription terminée",
                description: "Votre compte a été vérifié après un second essai. Vous êtes maintenant connecté!",
              });
              setTimeout(() => navigate("/"), 2000);
            } else {
              setVerificationStatus("error");
              toast({
                title: "Problème de vérification",
                description: "Impossible de vérifier votre compte. Veuillez essayer de vous connecter ou demander un nouveau lien.",
                variant: "destructive",
              });
              setTimeout(() => navigate("/auth"), 3000);
            }
            setIsLoading(false);
          }, 2000);
        }
      } else {
        // Handle hash params for other auth methods
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get("access_token");
        
        if (accessToken) {
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            throw error;
          }
          
          if (data?.session) {
            setVerificationStatus("success");
            toast({
              title: "Connexion réussie",
              description: "Vous êtes maintenant connecté!",
            });
            setTimeout(() => navigate("/"), 2000);
            return;
          }
        } else {
          // Regular check for an existing session
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            throw error;
          }
          
          if (data?.session) {
            setVerificationStatus("success");
            toast({
              title: "Authentification réussie",
              description: "Vous êtes maintenant connecté!",
            });
            setTimeout(() => navigate("/"), 2000);
            return;
          } else {
            setVerificationStatus("error");
            toast({
              title: "Session expirée",
              description: "Votre lien a expiré ou est invalide. Veuillez vous reconnecter.",
              variant: "destructive",
            });
            setTimeout(() => navigate("/auth"), 3000);
            return;
          }
        }
      }
    } catch (error) {
      console.error("Erreur dans handleAuthCallback:", error);
      setVerificationStatus("error");
      toast({
        title: "Erreur inattendue",
        description: "Une erreur s'est produite. Veuillez réessayer.",
        variant: "destructive",
      });
      setTimeout(() => navigate("/auth"), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleAuthCallback();
  }, []);

  return {
    isLoading,
    verificationStatus,
    showPasswordForm,
  };
};
